import type { ReactElement, RefObject } from "react";
import { createPortal } from "react-dom";
import type { ItemRarity } from "odyssey-shared";
import { Glyph, ItemSlot, logical, type DropTone, type GlyphName, type IconArt } from "../../ui/kit/index.ts";

/**
 * The thing a drag carries, drawn under the pointer (P2.8-T14; the design's `DragGhost`): the kit's item slot holding
 * the dragged item's art or glyph and its rarity's ring and pips (ADR-125 decision 1.2), never its count. The design draws it at 20; it is drawn at the bag's 24, since the
 * icon atlas holds an item's art for a 24 slot and a 16 glyph, and none for 20, so a 20 would swap the art the player
 * picked up for its glyph. Its place is written straight to its style by whoever moves it (`placeGhost`), so a pointer
 * moving re-renders nothing. Over a target it carries the design's marker, an arrow at its lower right corner in the
 * positive colour where the drop would land and in the danger colour where it would be put back, and none elsewhere;
 * the design's 8 px arrow at 14,14 of a 20 slot is the kit's 6 px arrow centred in the same 8 px box at 18,18 of the
 * 24, since the glyph set holds the arrow at 12 and 6 and not at 8. Where the page has a HUD layer the ghost is drawn in
 * it, lifted over every window, so a window that scrolls its content never clips it: a tool carried out of the
 * Inventory to the tool belt's chip stays in view (ADR-142 decision 3b). Without a layer it stands where it is rendered.
 */

/**
 * The ghost's size in logical pixels: the bag's slot.
 */
export const DRAG_GHOST_SIZE = 24;

/**
 * Where the marker's 8 px box stands on the ghost, in logical pixels from its top left: the design's 14 of 20, as far
 * past the slot's corner on the 24.
 */
export const DRAG_MARKER_AT = 18;

/**
 * The ghost.
 *
 * @param props The element's `host` ref, which `placeGhost` moves; the item's accessible `label`; its `rarity`, null for an
 *   item the rules lack; its colour `art`, drawn
 *   when it was scaled for the ghost's box; its `glyph`; and the `tone` of the target under it, null over none.
 * @returns The ghost.
 * @example
 * <DragGhost host={ghost} label="Minor Healing Potion" rarity="common" art={null} glyph="drop" tone="positive" />
 */
export function DragGhost({ host, label, rarity, art, glyph, tone }: { readonly host: RefObject<HTMLDivElement | null>; readonly label: string; readonly rarity: ItemRarity | null; readonly art: IconArt | null; readonly glyph: GlyphName | null; readonly tone: DropTone | null }): ReactElement {
  const layer = typeof document === "undefined" ? null : document.querySelector(".ot-hud-layer");
  const ghost = (
    <div ref={host} className={layer === null ? "ot-drag-ghost" : "ot-drag-ghost is-lifted"} data-drag="ghost" aria-hidden="true">
      <ItemSlot label={label} rarity={rarity} art={art} glyph={glyph} size={DRAG_GHOST_SIZE} tabStop={false} />
      {tone === null ? null : (
        <span className={`ot-drag-ghost__marker is-${tone}`} data-drag="marker" data-tone={tone} style={{ left: logical(DRAG_MARKER_AT), top: logical(DRAG_MARKER_AT) }}>
          <Glyph name="arrow-right-small" />
        </span>
      )}
    </div>
  );
  return layer === null ? ghost : createPortal(ghost, layer);
}

/**
 * Puts a ghost's centre on a point of the page.
 *
 * @param ghost The ghost's element, or null before it is drawn.
 * @param x Client x in CSS pixels.
 * @param y Client y in CSS pixels.
 */
export function placeGhost(ghost: HTMLElement | null, x: number, y: number): void {
  if (ghost !== null) {
    const half = ghost.offsetWidth / 2;
    ghost.style.transform = `translate(${x - half}px, ${y - half}px)`;
  }
}
